import * as z from 'zod'

import type { ListPartnersParams } from '@/api/gen'

/**
 * URL search-param schema + serialization helpers for /admin/partners.
 *
 * The page is fully URL-driven (the only source of truth for filters, sort,
 * and pagination), so a marketing teammate can share a filtered view by URL,
 * refresh without losing context, and bookmark a saved cut. Two boundaries
 * live in this file:
 *
 *   1. `partnersSearchSchema` — the wire-side Zod schema, used as the route
 *      `validateSearch` validator. Every accepted facet has a sealed enum so
 *      typos and stale values don't reach the BE call.
 *
 *   2. `toListPartnersParams` — translates the validated route state into the
 *      Orval `ListPartnersParams` shape. Keeping the translation here (rather
 *      than scattered across the page) means the upcoming BE params from PR
 *      #2394 (`can_access_portal`, `temp_email`, `migrated`) each drop in as
 *      ONE schema entry + one mapping entry, with zero call-site churn.
 */

// ─── Sealed enums ──────────────────────────────────────────────────────────

// Mirrors the STATUS_OPTION_KEYS used by the status pill row. "ALL" is the
// implicit default and is NOT serialized — only narrowing values appear in
// the URL so the canonical default URL stays clean.
export const PARTNER_STATUSES = ['PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED', 'REJECTED'] as const
export type PartnerStatus = (typeof PARTNER_STATUSES)[number]

// Marketing-friendly account facet. The BE param is `paired: boolean`; the URL
// keeps a readable string so a shared link is human-legible.
export const ACCOUNT_FACETS = ['linked', 'unlinked'] as const
export type AccountFacet = (typeof ACCOUNT_FACETS)[number]

// Beta-access facet (portal access). BE param is `can_access_portal: boolean`
// (gto-wizard#2394); same readable-string convention as `account`.
export const BETA_FACETS = ['yes', 'no'] as const
export type BetaFacet = (typeof BETA_FACETS)[number]

// Fully-migrated facet — admin-confirmed reconciliation. BE param is
// `fully_migrated: boolean`. Distinct from `migrated` (legacy origin) which
// keys on `legacy_affiliate_id`; this one keys on admin sign-off. Same
// readable-string convention as the other tri-state facets.
export const FULLY_MIGRATED_FACETS = ['yes', 'no'] as const
export type FullyMigratedFacet = (typeof FULLY_MIGRATED_FACETS)[number]

// T&C accepted facet — BE param is `terms_accepted: boolean` (true =
// `terms_accepted_at` set, false = never accepted). Same tri-state convention.
export const TERMS_ACCEPTED_FACETS = ['yes', 'no'] as const
export type TermsAcceptedFacet = (typeof TERMS_ACCEPTED_FACETS)[number]

// Pre-built segments from the BE. Same set used in the old page header.
export const PARTNER_SEGMENTS = [
  'never_converted',
  'churned',
  'top_performers',
  'no_payout_method',
] as const
export type PartnerSegment = (typeof PARTNER_SEGMENTS)[number]

// BE-accepted sort_by values — discovered from
// gto-wizard/app/app/account/affiliates/api/v4/admin.py `valid_sort_fields`.
// Enabling a column to sort on anything outside this set returns the default
// `-created_at` ordering and silently drops the sort intent, so the column
// would appear "stuck" — sealing the enum prevents that.
export const SORT_FIELDS = [
  'created_at',
  'name',
  'orders',
  'customers',
  'revenue',
  'commissions',
  'last_order',
] as const
export type SortField = (typeof SORT_FIELDS)[number]

export const SORT_DIRS = ['asc', 'desc'] as const
export type SortDir = (typeof SORT_DIRS)[number]

// ─── Schema ────────────────────────────────────────────────────────────────

const optionalDateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD')
  .optional()

export const partnersSearchSchema = z.object({
  status: z.enum(PARTNER_STATUSES).optional(),
  tier: z.coerce.number().int().positive().optional(),
  account: z.enum(ACCOUNT_FACETS).optional(),
  beta: z.enum(BETA_FACETS).optional(),
  fullyMigrated: z.enum(FULLY_MIGRATED_FACETS).optional(),
  termsAccepted: z.enum(TERMS_ACCEPTED_FACETS).optional(),
  // ISO 3166-1 alpha-2, normalized to uppercase (idempotent, so the router's
  // re-parse of its own output stays stable).
  country: z
    .string()
    .regex(/^[A-Za-z]{2}$/, 'country must be an ISO alpha-2 code')
    .transform((v) => v.toUpperCase())
    .optional(),
  segment: z.enum(PARTNER_SEGMENTS).optional(),
  q: z.string().optional(),
  from: optionalDateString,
  to: optionalDateString,
  sort: z.enum(SORT_FIELDS).optional(),
  dir: z.enum(SORT_DIRS).optional(),
  page: z.coerce.number().int().positive().optional(),
})

export type PartnersSearch = z.infer<typeof partnersSearchSchema>

/**
 * The "everything at defaults" search. The route's `validateSearch` `.catch()`s
 * to this so an unparseable URL silently reverts to the canonical view instead of
 * throwing inside the router (the bare schema would otherwise hard-fail and the page
 * never paints). All values are `undefined` — they get stripped from the URL
 * by the router serializer.
 */
export const DEFAULT_PARTNERS_SEARCH: PartnersSearch = {
  status: undefined,
  tier: undefined,
  account: undefined,
  beta: undefined,
  fullyMigrated: undefined,
  termsAccepted: undefined,
  country: undefined,
  segment: undefined,
  q: undefined,
  from: undefined,
  to: undefined,
  sort: undefined,
  dir: undefined,
  page: undefined,
}

// ─── Wire-param mapping ────────────────────────────────────────────────────

/**
 * Translate the URL state into the Orval-generated `ListPartnersParams` shape.
 * Defaults page=1, page_size=defaultPageSize. Returns an object with only the
 * keys that actually carry a value so the generated query key stays stable
 * (Orval includes the params object in the key; adding `undefined` properties
 * produces a different key and re-fetches needlessly).
 */
export const toListPartnersParams = (
  search: PartnersSearch,
  defaultPageSize: number,
): ListPartnersParams => {
  // Typed against the Orval wire shape — a typo'd key (e.g. `sortby` / `pageSize`)
  // fails the type check at the boundary, not at the BE.
  const params: ListPartnersParams = {
    page: search.page ?? 1,
    page_size: defaultPageSize,
  }

  if (search.status !== undefined) {
    params.status = search.status
  }
  if (search.tier !== undefined) {
    params.tier = search.tier
  }
  if (search.account === 'linked') {
    params.paired = true
  } else if (search.account === 'unlinked') {
    params.paired = false
  }
  if (search.beta === 'yes') {
    params.can_access_portal = true
  } else if (search.beta === 'no') {
    params.can_access_portal = false
  }
  if (search.fullyMigrated === 'yes') {
    params.fully_migrated = true
  } else if (search.fullyMigrated === 'no') {
    params.fully_migrated = false
  }
  if (search.termsAccepted === 'yes') {
    params.terms_accepted = true
  } else if (search.termsAccepted === 'no') {
    params.terms_accepted = false
  }
  if (search.country !== undefined) {
    params.country = search.country
  }
  if (search.segment !== undefined) {
    params.segment = search.segment
  }
  if (search.q !== undefined && search.q !== '') {
    params.search = search.q
  }
  if (search.from !== undefined) {
    params.date_from = search.from
  }
  if (search.to !== undefined) {
    params.date_to = search.to
  }
  if (search.sort !== undefined) {
    params.sort_by = search.sort
    params.sort_dir = search.dir ?? 'desc'
  }

  return params
}

// ─── Active-facet bookkeeping ──────────────────────────────────────────────

/**
 * Which non-default facets are currently active. Used by the page to decide
 * whether to render the active-chip row and the "Clear all" affordance.
 * Search and date range are NOT counted — they live in the same toolbar row
 * but are removed in place (clearing the field / the picker), not via chips.
 */
export const activeFacetCount = (search: PartnersSearch): number => {
  let n = 0
  if (search.status !== undefined) {
    n += 1
  }
  if (search.tier !== undefined) {
    n += 1
  }
  if (search.account !== undefined) {
    n += 1
  }
  if (search.beta !== undefined) {
    n += 1
  }
  if (search.fullyMigrated !== undefined) {
    n += 1
  }
  if (search.termsAccepted !== undefined) {
    n += 1
  }
  if (search.country !== undefined) {
    n += 1
  }
  if (search.segment !== undefined) {
    n += 1
  }

  return n
}
